// AppPorts — the host's ONLY contact with the world.
//
// The pure component tree receives this record via context and calls these
// functions; it never touches `fetch` or a timer directly. The concrete
// implementation is built once at the impure mount (`httpMain.tsx` ->
// `httpPorts`, fetch against the running server), so the tree stays pure and
// testable: the inert `stubPorts` satisfies the same type by structural
// subtyping (no mock framework), which is how stories/tests mount the tree
// without a backend.
//
// The interface is DERIVED from what `AppShellHost` actually calls, not
// speculated. Each method returns `PortResult<A>` — the failure is PARSED once,
// at the boundary, into the shell's one typed error vocabulary (`ExternalError`);
// no port ever returns `undefined`/`null`/`[]` to mean "something failed".
//
// This file is the STABLE PUBLIC ENTRY: it composes `AppPorts` as the
// intersection of the 12 per-BC port slices under `./ports/`, and RE-EXPORTS
// every slice type + DTO so the consumers that pull them from `./ports.js`
// are unaffected by the split. Each slice owns its own type AND the DTOs that
// type speaks; the one DTO shared across slices (`MaterialId`) lives in
// `./ports/ids.js`. See .claude/analysis/ports-decomposition-plan-2026-08-26.md.
//
// SCOPE NOTE — settings does NOT get a port here. The Settings destination
// mounts a self-contained stateful harness (`SettingsView`) fed real identity/
// workspace data via props from the host (never fixture defaults), so it needs
// no data port. If it later reads real control-plane data, that is where new
// ports land. (Admin has no destination in tier 0 — no cloud plane exists.)

import { createContext, useContext } from "react";
import type { AnnotationPorts } from "./ports/annotation.js";
import type { BriefPorts } from "./ports/brief.js";
import type { ClipboardPorts } from "./ports/clipboard.js";
import type { ContextPorts } from "./ports/context.js";
import type { ConversationPorts } from "./ports/conversation.js";
import type { FeedbackPorts } from "./ports/feedback.js";
import type { GenerationPorts } from "./ports/generation.js";
import type { IamPorts } from "./ports/iam.js";
import type { JournalPorts } from "./ports/journal.js";
import type { LandingPorts } from "./ports/landing.js";
import type { LearningPorts } from "./ports/learning.js";
import type { MaterialEditingPorts } from "./ports/materialEditing.js";
import type { PackagingPorts } from "./ports/packaging.js";
import type { ProjectPorts } from "./ports/project.js";
import type { ProposalPorts } from "./ports/proposal.js";
import type { QualityPorts } from "./ports/quality.js";
import type { ResourcePorts } from "./ports/resource.js";
import type { SnapshotPorts } from "./ports/snapshot.js";
import type { TemplatePorts } from "./ports/template.js";
import type { VersioningPorts } from "./ports/versioning.js";
import type { WorkspacePorts } from "./ports/workspace.js";

// Re-export every slice type + the DTOs each slice owns, so the 16 consumers
// that import these from `./ports.js` keep compiling unchanged after the DTOs
// moved into their owning slice files.
export type { AnnotationPorts } from "./ports/annotation.js";
export type { AmbientItemDTO, ContextPorts } from "./ports/context.js";
export type {
    ConversationPorts,
    ConversationSummary,
    LandedProposal,
    OpeningMoveResult,
    TurnResult,
} from "./ports/conversation.js";
export type {
    GenerationJobSnapshot,
    GenerationPartSnapshot,
    GenerationPorts,
    GenerationSkeleton,
    NewerVersions,
    PartCheck,
    PartReview,
    SkeletonPart,
    StaleReason,
} from "./ports/generation.js";
export type {
    AuthAnonymous,
    AuthAuthenticated,
    AuthStatus,
    AuthUser,
    IamPorts,
    WorkspaceMember,
    WorkspaceRole,
    WorkspaceSummary,
} from "./ports/iam.js";
export type { MaterialId } from "./ports/ids.js";
export type {
    CreatedLearningContext,
    LearningContextSummary,
    LearningPorts,
    NewLearningContext,
} from "./ports/learning.js";
export type { MaterialEditingPorts } from "./ports/materialEditing.js";
export type {
    ExportReady,
    LoadedExport,
    PackagingPorts,
} from "./ports/packaging.js";
export type { ProposalPorts } from "./ports/proposal.js";
export type { QualityPorts } from "./ports/quality.js";
export type {
    AddedSource,
    AddSourceFile,
    CreatedMaterial,
    DeletedResource,
    LoadedFile,
    LoadedMaterial,
    LoadedSource,
    MaterialProvenance,
    ModulePartsChange,
    PartMoved,
    PromotedFile,
    ResourcePorts,
    RestoredSourceContent,
} from "./ports/resource.js";
// `PortResult<A>` lives in `./ports/result.js` (the tiny shared module every
// slice imports); re-exported here so the consumers that pull it from
// `./ports.js` are unaffected.
export type { PortResult } from "./ports/result.js";
export type { TemplatePorts } from "./ports/template.js";
export type {
    EntityHistoryMoment,
    FileHistoryView,
    HistoryView,
    VersioningPorts,
} from "./ports/versioning.js";
export type {
    ContentHit,
    ContentSearchResult,
    ExportItem,
    FileItem,
    Library,
    LibraryActivityEntry,
    LibraryActivityKind,
    LibraryBootHealth,
    LibraryIssue,
    LibraryItem,
    LibraryProposalsIssue,
    LibrarySemanticSummary,
    LibrarySummaryCurrent,
    MaterialItem,
    MaterialReadability,
    MaterialTemplate,
    ResourceFacts,
    SourceBibliography,
    SourceItem,
    WorkspacePorts,
} from "./ports/workspace.js";

// AppPorts is the intersection of the per-BC port slices. The host needs
// every port at once (a product, not a variant), so the umbrella that bundles
// the independently-owned slices is their `&`. An intersection of record types
// with DISJOINT keys is structurally identical to a flat record: every
// consumer and every implementation (httpPorts/stubPorts) keeps compiling by
// structural subtyping. Each slice lives in its own `./ports/<bc>.js`; the
// split follows the context map's BCs. See
// .claude/analysis/ports-decomposition-plan-2026-08-26.md.
export type AppPorts = ConversationPorts &
    ContextPorts &
    IamPorts &
    LearningPorts &
    WorkspacePorts &
    ResourcePorts &
    MaterialEditingPorts &
    ProposalPorts &
    TemplatePorts &
    PackagingPorts &
    QualityPorts &
    VersioningPorts &
    AnnotationPorts &
    SnapshotPorts &
    GenerationPorts &
    JournalPorts &
    ClipboardPorts &
    BriefPorts &
    LandingPorts &
    ProjectPorts &
    FeedbackPorts;

// Context carries no default: the mount always provides real ports, and
// `useAppPorts` throws if it doesn't — a wiring bug surfaced loudly at first use
// rather than silently no-oping against a stub.
const AppPortsContext = createContext<AppPorts | null>(null);

export const AppPortsProvider = AppPortsContext.Provider;

export const useAppPorts = (): AppPorts => {
    const ports = useContext(AppPortsContext);
    if (ports === null) {
        throw new Error(
            "AppPorts not provided — mount must wrap the host in AppPortsProvider",
        );
    }
    return ports;
};
